import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import LanguageZoneEditor from '../components/brand/LanguageZoneEditor';
import { api, ApiError } from '../services/api';
import type { BrandStrategy as BrandStrategyType, Envergure, GuardrailViolation, IndustryCode } from '@traaker/shared';
import { INDUSTRY_CODES, industryLabels, isIndustryCode } from '@traaker/shared';

const BRAND_STRATEGY_LIMITS = {
  industry: 80,
  clientPersonas: 500,
  geographicFocus: 200,
  description: 800,
  aliases: { maxItems: 10, maxCharsPerItem: 80 },
  differentiators: { maxItems: 10, maxCharsPerItem: 150 },
  scenarios: { maxItems: 15, maxCharsPerItem: 200 },
  keywords: { maxItems: 30, maxCharsPerItem: 60 },
  customerQuestions: { maxItems: 20, maxCharsPerItem: 200 },
  customerObjections: { maxItems: 20, maxCharsPerItem: 200 },
  avoidTopics: { maxItems: 20, maxCharsPerItem: 150 },
  targetLanguages: { maxItems: 10, maxCharsPerItem: 10 },
  existingAssets: {
    wikipediaUrl: 500,
    pressKitUrl: 500,
    notableMentions: { maxItems: 20, maxCharsPerItem: 300 },
  },
} as const;

const BUSINESS_MODEL_LABELS: Record<string, string> = {
  b2b: 'B2B',
  b2c: 'B2C',
  both: 'B2B & B2C',
};

const ENVERGURE_LABEL_KEYS: Record<Envergure, string> = {
  'hyper-local': 'brandStrategy.envergure.hyperLocal',
  'multi-local': 'brandStrategy.envergure.multiLocal',
  'mono-country': 'brandStrategy.envergure.monoCountry',
  'multi-country': 'brandStrategy.envergure.multiCountry',
  global: 'brandStrategy.envergure.global',
};

export default function BrandStrategy() {
  const { t, i18n } = useTranslation();
  const industryLabelMap = industryLabels(i18n.language);
  const { activeBrand, refreshBrands } = useBrand();
  const { user } = useAuth();
  const canManage =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Strategy fields
  const [aliases, setAliases] = useState<string[]>([]);
  const [industry, setIndustry] = useState<IndustryCode | ''>('');
  const [industryLegacy, setIndustryLegacy] = useState('');
  const [clientPersonas, setClientPersonas] = useState('');
  const [businessModel, setBusinessModel] = useState<'b2b' | 'b2c' | 'both' | ''>('');
  const [geographicFocus, setGeographicFocus] = useState('');
  const [description, setDescription] = useState('');
  const [differentiators, setDifferentiators] = useState<string[]>([]);
  const [scenarios, setScenarios] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [customerQuestions, setCustomerQuestions] = useState<string[]>([]);
  const [customerObjections, setCustomerObjections] = useState<string[]>([]);
  const [avoidTopics, setAvoidTopics] = useState<string[]>([]);
  const [envergure, setEnvergure] = useState<Envergure | ''>('');
  const [wikipediaUrl, setWikipediaUrl] = useState('');
  const [pressKitUrl, setPressKitUrl] = useState('');
  const [notableMentions, setNotableMentions] = useState<string[]>([]);

  // Chip input buffers
  const [aliasInput, setAliasInput] = useState('');
  const [diffInput, setDiffInput] = useState('');
  const [scenarioInput, setScenarioInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [questionInput, setQuestionInput] = useState('');
  const [objectionInput, setObjectionInput] = useState('');
  const [avoidInput, setAvoidInput] = useState('');
  const [mentionInput, setMentionInput] = useState('');

  const loadFromBrand = useCallback(() => {
    if (!activeBrand) return;
    const s = activeBrand.strategy;
    setAliases(s?.aliases || []);
    setIndustry(isIndustryCode(s?.industry) ? s.industry : '');
    setIndustryLegacy(s?.industryLegacy || (isIndustryCode(s?.industry) ? '' : (s?.industry ?? '')));
    setClientPersonas(s?.clientPersonas || '');
    setBusinessModel(s?.businessModel || '');
    setGeographicFocus(s?.geographicFocus || '');
    setDescription(s?.description || '');
    setDifferentiators(s?.differentiators || []);
    setScenarios(s?.scenarios || []);
    setKeywords(s?.keywords || []);
    setCustomerQuestions(s?.customerQuestions || []);
    setCustomerObjections(s?.customerObjections || []);
    setAvoidTopics(s?.avoidTopics || []);
    setEnvergure(s?.envergure || '');
    setWikipediaUrl(s?.existingAssets?.wikipediaUrl || '');
    setPressKitUrl(s?.existingAssets?.pressKitUrl || '');
    setNotableMentions(s?.existingAssets?.notableMentions || []);
  }, [activeBrand]);

  useEffect(() => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setFieldErrors({});
  }, [loadFromBrand]);

  const handleCancel = () => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setFieldErrors({});
  };

  const handleSave = async () => {
    if (!activeBrand) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});

    const hasAssets = wikipediaUrl || pressKitUrl || notableMentions.length > 0;
    const payload: Partial<BrandStrategyType> = {
      aliases,
      industry: industry || undefined,
      industryLegacy: industryLegacy || undefined,
      clientPersonas: clientPersonas || undefined,
      businessModel: businessModel || undefined,
      geographicFocus: geographicFocus || undefined,
      description: description || undefined,
      differentiators,
      scenarios,
      keywords,
      customerQuestions,
      customerObjections,
      avoidTopics,
      // targetLanguages is managed by LanguageZoneEditor (immediate save), not
      // this batch — omitting it here leaves the persisted value untouched.
      envergure: envergure || undefined,
      existingAssets: hasAssets
        ? {
            wikipediaUrl: wikipediaUrl || undefined,
            pressKitUrl: pressKitUrl || undefined,
            notableMentions: notableMentions.length > 0 ? notableMentions : undefined,
          }
        : undefined,
    };

    try {
      await api.patch(`/brands/${activeBrand.brandId}/strategy`, payload);
      await refreshBrands();
      setEditing(false);
    } catch (err: any) {
      if (err instanceof ApiError && err.details) {
        const violations = err.details as GuardrailViolation[];
        const errors: Record<string, string> = {};
        violations.forEach((v) => {
          // Map array field violations back to parent field
          const field = v.field.replace(/\[\d+\]$/, '');
          errors[field] = v.message;
        });
        setFieldErrors(errors);
        setError(t('brandStrategy.error.contentNotAllowed'));
      } else {
        setError(err.message || t('brandStrategy.error.save'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!activeBrand) return;
    setValidating(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/strategy/validate`, {});
      await refreshBrands();
      setInfo(t('brandStrategy.info.validated'));
    } catch (err: any) {
      setError(err.message || t('brandStrategy.error.validate'));
    } finally {
      setValidating(false);
    }
  };

  const handleChipAdd = (
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg?: { maxItems: number; maxCharsPerItem: number },
  ) => {
    const trimmed = value.trim();
    if (cfg) {
      if (list.length >= cfg.maxItems) {
        setInput('');
        return;
      }
      if (trimmed.length > cfg.maxCharsPerItem) return;
    }
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput('');
  };

  const handleChipKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg?: { maxItems: number; maxCharsPerItem: number },
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChipAdd(value, list, setList, setInput, cfg);
    }
  };

  const chipHelper = (
    list: string[],
    cfg: { maxItems: number; maxCharsPerItem: number },
    fieldError?: string,
  ) =>
    fieldError ||
    t('brandStrategy.chipHelper', {
      count: list.length,
      max: cfg.maxItems,
      chars: cfg.maxCharsPerItem,
    });

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          {t('brandStrategy.title')}
        </Typography>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('brandStrategy.noBrand')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const hasStrategy = activeBrand.strategy && Object.keys(activeBrand.strategy).length > 0;
  const brandMeta = activeBrand.strategyMeta;
  const brandStatus = brandMeta?.status;
  const STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning'; icon?: React.ReactElement }> = {
    draft: { label: t('brandStrategy.status.draft'), color: 'default' },
    validated: { label: t('brandStrategy.status.validated'), color: 'success', icon: <VerifiedIcon fontSize="small" /> },
    stale: { label: t('brandStrategy.status.stale'), color: 'warning' },
  };
  const statusChip = brandStatus ? STATUS_CHIPS[brandStatus] : null;

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          overflow: 'hidden',
          background: (theme) =>
            `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
              theme.palette.primary.light,
              0.03,
            )} 100%)`,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        }}
      >
        <CardContent
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <AutoStoriesOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {t('brandStrategy.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {activeBrand.name} {activeBrand.url && `— ${activeBrand.url}`}
                </Typography>
                {statusChip && (
                  <Box data-tour="strategy-status">
                    <Chip
                      size="small"
                      label={t('brandStrategy.statusChip', { status: statusChip.label })}
                      color={statusChip.color}
                      icon={statusChip.icon}
                      variant="outlined"
                      sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}
            data-tour="strategy-actions"
          >
            {canManage && !editing && hasStrategy && brandStatus !== 'validated' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleValidate}
                disabled={validating}
              >
                {validating ? t('brandStrategy.action.validating') : t('brandStrategy.action.validate')}
              </Button>
            )}
            {canManage && !editing && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                {t('brandStrategy.action.edit')}
              </Button>
            )}
            {canManage && editing && (
              <>
                <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancel}>
                  {t('brandStrategy.action.cancel')}
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                  {t('brandStrategy.action.save')}
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {!hasStrategy && !editing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('brandStrategy.noStrategy')}{' '}
          {canManage ? t('brandStrategy.noStrategyAdmin') : t('brandStrategy.noStrategyNonAdmin')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {hasStrategy && brandStatus === 'stale' && !editing && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('brandStrategy.alert.stale')}
        </Alert>
      )}
      {hasStrategy && !brandStatus && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('brandStrategy.alert.notValidated')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Identity */}
        <Grid size={12}>
          <SectionCard icon={<BadgeOutlinedIcon />} title={t('brandStrategy.section.identity')} usedBy={t('brandStrategy.usedBy.emBpVeille')}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.brandName')} />
                <Typography>{activeBrand.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.website')} />
                <Typography>{activeBrand.url || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.industry')} />
                {editing ? (
                  <>
                    <Select
                      fullWidth
                      size="small"
                      displayEmpty
                      value={industry}
                      onChange={(e) => setIndustry((e.target.value || '') as IndustryCode | '')}
                      renderValue={(v) =>
                        v ? industryLabelMap[v as IndustryCode] : <span style={{ color: '#9aa' }}>{t('brandStrategy.industryPlaceholder')}</span>
                      }
                    >
                      {INDUSTRY_CODES.map((code) => (
                        <MenuItem key={code} value={code}>{industryLabelMap[code]}</MenuItem>
                      ))}
                    </Select>
                    {industryLegacy && !industry && (
                      <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                        {t('brandStrategy.industryLegacy', { legacy: industryLegacy })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography>{industry ? industryLabelMap[industry] : (industryLegacy || '—')}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.aliases')} />
                {editing ? (
                  <Box>
                    <ChipList items={aliases} onDelete={(i) => setAliases(aliases.filter((_, idx) => idx !== i))} />
                    <TextField
                      fullWidth
                      size="small"
                      value={aliasInput}
                      onChange={(e) =>
                        setAliasInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem))
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          aliasInput,
                          aliases,
                          setAliases,
                          setAliasInput,
                          BRAND_STRATEGY_LIMITS.aliases,
                        )
                      }
                      placeholder={t('brandStrategy.placeholder.typeEnter')}
                      inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem }}
                      helperText={chipHelper(aliases, BRAND_STRATEGY_LIMITS.aliases)}
                      disabled={aliases.length >= BRAND_STRATEGY_LIMITS.aliases.maxItems}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={aliases} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Target Market */}
        <Grid size={12}>
          <SectionCard icon={<GroupsOutlinedIcon />} title={t('brandStrategy.section.market')} usedBy={t('brandStrategy.usedBy.bpQueryGen')}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.clientPersonas')} />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={clientPersonas}
                    onChange={(e) =>
                      setClientPersonas(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.clientPersonas))
                    }
                    placeholder={t('brandStrategy.placeholder.clientPersonas')}
                    error={!!fieldErrors.clientPersonas}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.clientPersonas }}
                    helperText={
                      fieldErrors.clientPersonas ||
                      `${clientPersonas.length} / ${BRAND_STRATEGY_LIMITS.clientPersonas}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{clientPersonas || '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FieldLabel label={t('brandStrategy.field.businessModel')} />
                {editing ? (
                  <Select
                    fullWidth
                    size="small"
                    value={businessModel}
                    onChange={(e) => setBusinessModel(e.target.value as any)}
                    displayEmpty
                  >
                    <MenuItem value="">{t('brandStrategy.businessModel.undefined')}</MenuItem>
                    <MenuItem value="b2b">B2B</MenuItem>
                    <MenuItem value="b2c">B2C</MenuItem>
                    <MenuItem value="both">B2B & B2C</MenuItem>
                  </Select>
                ) : (
                  <Typography>{businessModel ? BUSINESS_MODEL_LABELS[businessModel] : '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FieldLabel label={t('brandStrategy.field.geographicFocus')} />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={geographicFocus}
                    onChange={(e) =>
                      setGeographicFocus(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.geographicFocus))
                    }
                    placeholder={t('brandStrategy.placeholder.geographicFocus')}
                    error={!!fieldErrors.geographicFocus}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.geographicFocus }}
                    helperText={
                      fieldErrors.geographicFocus ||
                      `${geographicFocus.length} / ${BRAND_STRATEGY_LIMITS.geographicFocus}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{geographicFocus || '—'}</Typography>
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Value Proposition */}
        <Grid size={12}>
          <SectionCard icon={<WorkspacePremiumOutlinedIcon />} title={t('brandStrategy.section.valueProp')} usedBy={t('brandStrategy.usedBy.emBpVeille')}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FieldLabel label={t('brandStrategy.field.description')} />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.description))
                    }
                    placeholder={t('brandStrategy.placeholder.description')}
                    error={!!fieldErrors.description}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.description }}
                    helperText={
                      fieldErrors.description ||
                      `${description.length} / ${BRAND_STRATEGY_LIMITS.description}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{description || '—'}</Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FieldLabel label={t('brandStrategy.field.differentiators')} />
                {editing ? (
                  <Box>
                    <ChipList items={differentiators} onDelete={(i) => setDifferentiators(differentiators.filter((_, idx) => idx !== i))} />
                    <TextField
                      fullWidth
                      size="small"
                      value={diffInput}
                      onChange={(e) =>
                        setDiffInput(
                          e.target.value.slice(0, BRAND_STRATEGY_LIMITS.differentiators.maxCharsPerItem),
                        )
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          diffInput,
                          differentiators,
                          setDifferentiators,
                          setDiffInput,
                          BRAND_STRATEGY_LIMITS.differentiators,
                        )
                      }
                      placeholder={t('brandStrategy.placeholder.typeEnter')}
                      error={!!fieldErrors.differentiators}
                      inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.differentiators.maxCharsPerItem }}
                      helperText={chipHelper(
                        differentiators,
                        BRAND_STRATEGY_LIMITS.differentiators,
                        fieldErrors.differentiators,
                      )}
                      disabled={differentiators.length >= BRAND_STRATEGY_LIMITS.differentiators.maxItems}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={differentiators} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Sales Plays */}
        <Grid size={12}>
          <SectionCard icon={<CampaignOutlinedIcon />} title={t('brandStrategy.section.scenarios')} usedBy={t('brandStrategy.usedBy.bpVeille')}>
            <FieldLabel label={t('brandStrategy.field.scenarios')} />
            {editing ? (
              <Box>
                <ChipList items={scenarios} onDelete={(i) => setScenarios(scenarios.filter((_, idx) => idx !== i))} />
                <TextField
                  fullWidth
                  size="small"
                  value={scenarioInput}
                  onChange={(e) =>
                    setScenarioInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.scenarios.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      scenarioInput,
                      scenarios,
                      setScenarios,
                      setScenarioInput,
                      BRAND_STRATEGY_LIMITS.scenarios,
                    )
                  }
                  placeholder={t('brandStrategy.placeholder.scenarios')}
                  error={!!fieldErrors.scenarios}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.scenarios.maxCharsPerItem }}
                  helperText={chipHelper(scenarios, BRAND_STRATEGY_LIMITS.scenarios, fieldErrors.scenarios)}
                  disabled={scenarios.length >= BRAND_STRATEGY_LIMITS.scenarios.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={scenarios} />
            )}
          </SectionCard>
        </Grid>

        {/* Topics to Own */}
        <Grid size={12}>
          <SectionCard icon={<SellOutlinedIcon />} title={t('brandStrategy.section.keywords')} usedBy={t('brandStrategy.usedBy.emBpVeille')}>
            <FieldLabel label={t('brandStrategy.field.keywords')} />
            {editing ? (
              <Box>
                <ChipList items={keywords} onDelete={(i) => setKeywords(keywords.filter((_, idx) => idx !== i))} />
                <TextField
                  fullWidth
                  size="small"
                  value={keywordInput}
                  onChange={(e) =>
                    setKeywordInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      keywordInput,
                      keywords,
                      setKeywords,
                      setKeywordInput,
                      BRAND_STRATEGY_LIMITS.keywords,
                    )
                  }
                  placeholder={t('brandStrategy.placeholder.keywords')}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem }}
                  helperText={chipHelper(keywords, BRAND_STRATEGY_LIMITS.keywords)}
                  disabled={keywords.length >= BRAND_STRATEGY_LIMITS.keywords.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={keywords} />
            )}
          </SectionCard>
        </Grid>

        {/* Customer Questions — pre-purchase */}
        <Grid size={12}>
          <SectionCard
            icon={<HelpOutlineIcon />}
            title={t('brandStrategy.section.customerQuestions')}
            usedBy={t('brandStrategy.usedBy.bpReferralVeille')}
          >
            <FieldLabel label={t('brandStrategy.field.customerQuestions')} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('brandStrategy.hint.customerQuestions')}
            </Typography>
            {editing ? (
              <Box>
                <ChipList
                  items={customerQuestions}
                  onDelete={(i) => setCustomerQuestions(customerQuestions.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={questionInput}
                  onChange={(e) =>
                    setQuestionInput(
                      e.target.value.slice(0, BRAND_STRATEGY_LIMITS.customerQuestions.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      questionInput,
                      customerQuestions,
                      setCustomerQuestions,
                      setQuestionInput,
                      BRAND_STRATEGY_LIMITS.customerQuestions,
                    )
                  }
                  placeholder={t('brandStrategy.placeholder.customerQuestions')}
                  error={!!fieldErrors.customerQuestions}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.customerQuestions.maxCharsPerItem }}
                  helperText={chipHelper(
                    customerQuestions,
                    BRAND_STRATEGY_LIMITS.customerQuestions,
                    fieldErrors.customerQuestions,
                  )}
                  disabled={customerQuestions.length >= BRAND_STRATEGY_LIMITS.customerQuestions.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={customerQuestions} />
            )}
          </SectionCard>
        </Grid>

        {/* Customer Objections — post-awareness blockers */}
        <Grid size={12}>
          <SectionCard
            icon={<GppMaybeOutlinedIcon />}
            title={t('brandStrategy.section.customerObjections')}
            usedBy={t('brandStrategy.usedBy.emValidationBpNegative')}
          >
            <FieldLabel label={t('brandStrategy.field.customerObjections')} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('brandStrategy.hint.customerObjections')}
            </Typography>
            {editing ? (
              <Box>
                <ChipList
                  items={customerObjections}
                  onDelete={(i) => setCustomerObjections(customerObjections.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={objectionInput}
                  onChange={(e) =>
                    setObjectionInput(
                      e.target.value.slice(0, BRAND_STRATEGY_LIMITS.customerObjections.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      objectionInput,
                      customerObjections,
                      setCustomerObjections,
                      setObjectionInput,
                      BRAND_STRATEGY_LIMITS.customerObjections,
                    )
                  }
                  placeholder={t('brandStrategy.placeholder.customerObjections')}
                  error={!!fieldErrors.customerObjections}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.customerObjections.maxCharsPerItem }}
                  helperText={chipHelper(
                    customerObjections,
                    BRAND_STRATEGY_LIMITS.customerObjections,
                    fieldErrors.customerObjections,
                  )}
                  disabled={customerObjections.length >= BRAND_STRATEGY_LIMITS.customerObjections.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={customerObjections} />
            )}
          </SectionCard>
        </Grid>

        {/* Avoid Topics */}
        <Grid size={12}>
          <SectionCard
            icon={<BlockOutlinedIcon />}
            title={t('brandStrategy.section.avoidTopics')}
            usedBy={t('brandStrategy.usedBy.emFilterBpNegative')}
          >
            <FieldLabel label={t('brandStrategy.field.avoidTopics')} />
            {editing ? (
              <Box>
                <ChipList
                  items={avoidTopics}
                  onDelete={(i) => setAvoidTopics(avoidTopics.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={avoidInput}
                  onChange={(e) =>
                    setAvoidInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.avoidTopics.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      avoidInput,
                      avoidTopics,
                      setAvoidTopics,
                      setAvoidInput,
                      BRAND_STRATEGY_LIMITS.avoidTopics,
                    )
                  }
                  placeholder={t('brandStrategy.placeholder.avoidTopics')}
                  error={!!fieldErrors.avoidTopics}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.avoidTopics.maxCharsPerItem }}
                  helperText={chipHelper(avoidTopics, BRAND_STRATEGY_LIMITS.avoidTopics, fieldErrors.avoidTopics)}
                  disabled={avoidTopics.length >= BRAND_STRATEGY_LIMITS.avoidTopics.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={avoidTopics} />
            )}
          </SectionCard>
        </Grid>

        {/* Envergure (current geographic footprint) */}
        <Grid size={12}>
          <SectionCard icon={<PublicOutlinedIcon />} title={t('brandStrategy.section.envergure')} usedBy={t('brandStrategy.usedBy.bpEm')}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.currentEnvergure')} />
                {editing ? (
                  <>
                    <Select
                      fullWidth
                      size="small"
                      value={envergure}
                      onChange={(e) => setEnvergure(e.target.value as Envergure | '')}
                      displayEmpty
                    >
                      <MenuItem value="">{t('brandStrategy.envergure.undefined')}</MenuItem>
                      {Object.entries(ENVERGURE_LABEL_KEYS).map(([val, labelKey]) => (
                        <MenuItem key={val} value={val}>
                          {t(labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('brandStrategy.hint.envergure')}
                    </Typography>
                  </>
                ) : (
                  <Typography>{envergure ? t(ENVERGURE_LABEL_KEYS[envergure]) : '—'}</Typography>
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Existing Assets */}
        <Grid size={12}>
          <SectionCard icon={<CollectionsBookmarkOutlinedIcon />} title={t('brandStrategy.section.existingAssets')} usedBy={t('brandStrategy.usedBy.emReliableSources')}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.wikipediaUrl')} />
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={wikipediaUrl}
                    onChange={(e) =>
                      setWikipediaUrl(
                        e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl),
                      )
                    }
                    placeholder="https://fr.wikipedia.org/wiki/..."
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl }}
                  />
                ) : (
                  <Typography sx={{ wordBreak: 'break-all' }}>{wikipediaUrl || '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label={t('brandStrategy.field.pressKitUrl')} />
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={pressKitUrl}
                    onChange={(e) =>
                      setPressKitUrl(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl))
                    }
                    placeholder="https://..."
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl }}
                  />
                ) : (
                  <Typography sx={{ wordBreak: 'break-all' }}>{pressKitUrl || '—'}</Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FieldLabel label={t('brandStrategy.field.notableMentions')} />
                {editing ? (
                  <Box>
                    <ChipList
                      items={notableMentions}
                      onDelete={(i) => setNotableMentions(notableMentions.filter((_, idx) => idx !== i))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={mentionInput}
                      onChange={(e) =>
                        setMentionInput(
                          e.target.value.slice(
                            0,
                            BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxCharsPerItem,
                          ),
                        )
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          mentionInput,
                          notableMentions,
                          setNotableMentions,
                          setMentionInput,
                          BRAND_STRATEGY_LIMITS.existingAssets.notableMentions,
                        )
                      }
                      placeholder={t('brandStrategy.placeholder.notableMentions')}
                      error={!!fieldErrors['existingAssets.notableMentions']}
                      inputProps={{
                        maxLength: BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxCharsPerItem,
                      }}
                      helperText={chipHelper(
                        notableMentions,
                        BRAND_STRATEGY_LIMITS.existingAssets.notableMentions,
                        fieldErrors['existingAssets.notableMentions'],
                      )}
                      disabled={
                        notableMentions.length >=
                        BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxItems
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={notableMentions} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Langues & Localisations — onboarding-style editor (immediate save). */}
        <Grid size={12}>
          <SectionCard
            icon={<TranslateOutlinedIcon />}
            title={t('brandStrategy.section.languagesLocations')}
            usedBy={t('brandStrategy.usedBy.allAuditsPrompts')}
          >
            {canManage ? (
              <LanguageZoneEditor brand={activeBrand} />
            ) : (
              <Alert severity="info">
                {t('brandStrategy.languagesAdminOnly')}
              </Alert>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Saving overlay */}
      {saving && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: '#070B14CC',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.primary">
            {t('brandStrategy.savingOverlay')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Helper components ──

function SectionCard({
  icon,
  title,
  usedBy,
  headerRight,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  usedBy?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card
      sx={{
        height: '100%',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            mb: 2.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
            {icon && (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  '& svg': { fontSize: 20 },
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                {title}
              </Typography>
              {usedBy && (
                <Typography variant="caption" color="text.secondary">
                  {t('brandStrategy.usedByLabel', { value: usedBy })}
                </Typography>
              )}
            </Box>
          </Box>
          {headerRight}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        mb: 0.75,
        display: 'block',
        color: 'text.secondary',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </Typography>
  );
}

function ChipList({
  items,
  onDelete,
}: {
  items: string[];
  onDelete?: (index: number) => void;
}) {
  if (items.length === 0 && !onDelete) {
    return (
      <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {items.map((item, i) => (
        <Chip
          key={`${item}-${i}`}
          label={item}
          size="small"
          onDelete={onDelete ? () => onDelete(i) : undefined}
          sx={{
            fontWeight: 500,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.dark',
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
            '& .MuiChip-deleteIcon': {
              color: (theme) => alpha(theme.palette.primary.main, 0.5),
              '&:hover': { color: 'primary.main' },
            },
          }}
        />
      ))}
    </Box>
  );
}
